/**
 * Displays GI (Group Inbox) screenshot versions with per-version stats
 * (extracted, processed, unique, duplicate orders) and zoomable screenshots.
 * @module giScreenshotViewer
 */

import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Zoom from "react-medium-image-zoom";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../api/firebase/firestoreService";
import { LoaderMessage } from "../loaderMessage/loaderMessage";
import NoData from "../noData/noData";

interface GiVersionData {
  last_updated: Date;
  run_id: string;
  screenshot_path: string;
  unique_orders_stored: number;
  latest_extracted_orders: number;
  latest_orders_processed: number;
  duplicate_orders_stored: number;
}

interface GiScreenshotViewerProps {
  versionsData?: Record<string, GiVersionData>;
  totalUniqueOrdersStored?: number;
  showLoading?: boolean;
}

interface ProcessedImageData {
  versionId: string;
  imagePath: string;
  runId: string;
  ordersStored: number;
  lastUpdated: string;
  latestExtractedOrders: number;
  latestOrdersProcessed: number;
  uniqueOrdersStored: number;
  duplicateOrdersStored: number;
}

const GiScreenshotViewer = (props: GiScreenshotViewerProps) => {
  const { versionsData, totalUniqueOrdersStored, showLoading } = props;

  const processedImages: ProcessedImageData[] = useMemo(() => {
    if (!versionsData) return [];

    return Object.entries(versionsData).map(([versionId, data]) => ({
      versionId,
      imagePath: data.screenshot_path,
      runId: data.run_id,
      ordersStored: data.unique_orders_stored,
      lastUpdated: moment(data.last_updated).format("MM/DD/YYYY HH:mm"),
      latestExtractedOrders: data.latest_extracted_orders,
      latestOrdersProcessed: data.latest_orders_processed,
      uniqueOrdersStored: data.unique_orders_stored,
      duplicateOrdersStored: data.duplicate_orders_stored,
    }));
  }, [versionsData]);

  return (
    <div className="gi-screenshot-viewer-container flex flex-col">
      {showLoading ? (
        <div className="loader-container flex h-full w-full items-center justify-center">
          <LoaderMessage message="Loading GI screenshots..." />
        </div>
      ) : (
        <div className="gi-screenshot-viewer-content flex flex-col gap-2">
          {totalUniqueOrdersStored !== undefined && (
            <div className="mb-3 flex items-center justify-between rounded-md border border-primaryGray-15 px-4 py-2.5 shadow-sm">
              <span className="text-xs font-semibold text-primaryGray-1">
                Total Unique Orders Stored
              </span>
              <span className="rounded-full bg-blue-600 px-2.5 py-0.5 text-xs font-bold text-white">
                {totalUniqueOrdersStored}
              </span>
            </div>
          )}
          {processedImages && processedImages.length > 0 ? (
            <>
              {processedImages.map((imageData, index) => (
                <GiScreenshotItem
                  key={`${imageData.versionId}_${imageData.runId}`}
                  versionId={imageData.versionId}
                  versionIndex={index + 1}
                  imagePath={imageData.imagePath}
                  runId={imageData.runId}
                  ordersStored={imageData.ordersStored}
                  lastUpdated={imageData.lastUpdated}
                  latestExtractedOrders={imageData.latestExtractedOrders}
                  latestOrdersProcessed={imageData.latestOrdersProcessed}
                  uniqueOrdersStored={imageData.uniqueOrdersStored}
                  duplicateOrdersStored={imageData.duplicateOrdersStored}
                  shouldShowBottomBorder={index !== processedImages.length - 1}
                />
              ))}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <NoData text="No GI screenshot data found" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface GiScreenshotItemProps {
  versionId: string;
  versionIndex: number;
  imagePath: string;
  runId: string;
  ordersStored: number;
  lastUpdated: string;
  latestExtractedOrders: number;
  latestOrdersProcessed: number;
  uniqueOrdersStored: number;
  duplicateOrdersStored: number;
  shouldShowBottomBorder?: boolean;
}

const GiScreenshotItem = (props: GiScreenshotItemProps) => {
  const {
    versionId,
    versionIndex,
    imagePath,
    lastUpdated,
    latestExtractedOrders,
    latestOrdersProcessed,
    uniqueOrdersStored,
    duplicateOrdersStored,
    shouldShowBottomBorder,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>("");

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const getImageUrl = useCallback(async () => {
    try {
      const url = await getFileDownloadUrl(imagePath);
      setImageUrl(url);
    } catch (error) {
      console.error("Error fetching GI screenshot URL:", error);
      throw error;
    }
  }, [imagePath]);

  useEffect(() => {
    let cancelled = false;

    if (imagePath && imagePath.trim() !== "") {
      setIsLoading(true);
      getImageUrl()
        .then(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error("Error in image loading:", error);
            setIsLoading(false);
          }
        });
    } else {
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [imagePath, getImageUrl]);

  return (
    <>
      <div className="mb-2 overflow-hidden rounded-md border border-primaryGray-15 bg-primaryGray-16">
        {/* Header */}
        <div
          className="flex items-center justify-between border-b border-primaryGray-15 bg-white px-3 py-2"
          title={versionId}
        >
          <div className="flex items-center gap-2">
            <span className="rounded bg-[#E6F3F0] px-2 py-0.5 text-[11px] font-bold text-[#005D49]">
              v{versionIndex}
            </span>
            <span className="text-xs text-primaryGray-6">
              {versionId.slice(0, 8)}
            </span>
          </div>
          <span className="text-[10px] text-primaryGray-6">{lastUpdated}</span>
        </div>

        {/* Stats */}
        <div className="p-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {[
              { label: "Extracted", value: latestExtractedOrders },
              { label: "Processed", value: latestOrdersProcessed },
              {
                label: "Unique",
                value: uniqueOrdersStored,
                color: "text-green-700",
              },
              {
                label: "Duplicates",
                value: duplicateOrdersStored,
                color: "text-amber-600",
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="flex items-center justify-between rounded bg-white px-2.5 py-1.5 shadow-sm"
              >
                <span className="text-[11px] text-primaryGray-6">{label}</span>
                <span
                  className={`text-xs font-semibold ${color ?? "text-primaryGray-1"}`}
                >
                  {value ?? 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Screenshot */}
        <div className="relative border-t border-primaryGray-15 p-3">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
              <SpinningLoader />
            </div>
          )}
          <Zoom>
            <img
              src={imageUrl}
              alt={`GI Screenshot Version ${versionId}`}
              onLoad={handleImageLoad}
              className="block w-full"
              style={{ opacity: isLoading ? 0 : 1 }}
            />
          </Zoom>
        </div>
      </div>

      {shouldShowBottomBorder && (
        <div className="mb-2 border-b-1 border-dashed border-primaryGray-14"></div>
      )}
    </>
  );
};

export default GiScreenshotViewer;
